import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import http from '../api/http'
import adminWebSocketService from '../services/websocket'

export const useUserStore = defineStore('user', () => {
  // 從sessionStorage恢復用戶信息（支援多帳號同時登入）
  const savedUser = sessionStorage.getItem('admin_user')
  const user = ref(savedUser ? JSON.parse(savedUser) : null)
  const token = ref(sessionStorage.getItem('admin_token') || '')
  
  const isLoggedIn = computed(() => !!token.value)
  
  function setToken(newToken) {
    token.value = newToken
    sessionStorage.setItem('admin_token', newToken)
  }
  
  function setUser(userData) {
    user.value = userData
    // 將用戶信息保存到sessionStorage（支援多帳號同時登入）
    if (userData) {
      sessionStorage.setItem('admin_user', JSON.stringify(userData))
    } else {
      sessionStorage.removeItem('admin_user')
    }
  }
  
  // 最近一次 token 驗證成功的時間；短時間內的路由切換不重複打驗證 API
  const TOKEN_VALIDATION_TTL = 60 * 1000
  let lastValidatedAt = 0

  // 「記住我」只保存續期票與帳號，不保存密碼
  const REFRESH_SESSION_KEY = 'admin_refresh_session'

  function getSavedRefreshSession() {
    try {
      const saved = localStorage.getItem(REFRESH_SESSION_KEY)
      if (!saved) return null

      const session = JSON.parse(saved)
      return (session?.refreshToken && session?.database) ? session : null
    } catch {
      return null
    }
  }

  function saveRefreshSession(session) {
    try {
      localStorage.setItem(REFRESH_SESSION_KEY, JSON.stringify(session))
    } catch (error) {
      console.error('保存續期票失敗:', error)
    }
  }

  function clearSavedRefreshSession() {
    localStorage.removeItem(REFRESH_SESSION_KEY)
  }

  // 本分頁沒有 token 時，仍可能有「記住我」的續期票可用
  function hasSavedRefreshSession() {
    return !!getSavedRefreshSession()
  }

  // 驗證token有效性
  async function validateToken() {
    if (!token.value) return false

    // 短時間內已驗證過就直接放行，避免每次切換頁面都多等一次 API 往返
    // 若 token 在期間失效，後續 API 的 401 攔截器會自動導回登入頁
    if (Date.now() - lastValidatedAt < TOKEN_VALIDATION_TTL) {
      return true
    }

    try {
      const response = await http.get('/admin/validate-token')
      if (response.data.valid && response.data.user) {
        // Token有效，更新用戶信息
        setUser(response.data.user)
        lastValidatedAt = Date.now()
        return true
      } else {
        // Token無效，改用續期票換發
        return await attemptTokenRefresh()
      }
    } catch (error) {
      console.error('Token驗證失敗:', error)
      return await attemptTokenRefresh()
    }
  }

  // 用續期票換發新 token，過程中不需要密碼
  async function attemptTokenRefresh() {
    try {
      const saved = getSavedRefreshSession()
      if (saved) {
        const response = await http.post('/admin/refresh-token', {
          refreshToken: saved.refreshToken,
          database: saved.database
        })

        if (response.data.success) {
          const { token: newToken, username, role, database, databaseName } = response.data
          setToken(newToken)
          setUser({ username, role, database, databaseName })
          lastValidatedAt = Date.now()

          // 重新初始化WebSocket連接
          await initWebSocket()

          return true
        }
      }
    } catch (refreshError) {
      console.warn('續期失敗:', refreshError)
      // 票已失效或被作廢就不再保留
      if (refreshError.response?.status === 401) {
        clearSavedRefreshSession()
      }
    }

    // 只清掉本分頁的登入狀態，續期票留著；若是暫時性錯誤，下次還能再續
    clearSession()
    return false
  }
  
  // 初始化WebSocket連接
  async function initWebSocket() {
    if (!token.value) return
    
    try {
      await adminWebSocketService.connect(token.value)
    } catch (error) {
      // WebSocket 連接失敗時，服務會自動嘗試重連
      // 這裡不需要額外處理，避免重複連接
    }
  }
  
  async function login(credentials) {
    try {
      const response = await http.post('/admin/login', {
        ...credentials,
        deviceId: adminWebSocketService.getDeviceId()
      })
      const { token: newToken, refreshToken, username, role, database, databaseName } = response.data

      setToken(newToken)
      setUser({ username, role, database, databaseName })
      lastValidatedAt = Date.now()

      // 勾了記住我才會拿到續期票
      if (refreshToken) {
        saveRefreshSession({ refreshToken, username, database })
      } else {
        clearSavedRefreshSession()
      }

      // 登入成功後立即初始化WebSocket連接
      await initWebSocket()

      return { success: true }
    } catch (error) {
      console.error('登入失敗:', error)
      return { 
        success: false, 
        message: error.response?.data?.error || '登入失敗，請稍後再試' 
      }
    }
  }
  
  // 清除本分頁的登入狀態，不動「記住我」的續期票
  function clearSession() {
    adminWebSocketService.disconnect()

    lastValidatedAt = 0
    token.value = ''
    user.value = null
    // 只清除當前分頁的認證資訊（支援多帳號同時登入）
    sessionStorage.removeItem('admin_token')
    sessionStorage.removeItem('admin_user')
  }

  // 使用者主動登出：一併作廢續期票，之後這台設備不能再自動續期
  function logout() {
    const saved = getSavedRefreshSession()
    if (saved) {
      http.post('/admin/revoke-refresh-token', {
        refreshToken: saved.refreshToken,
        database: saved.database
      }).catch(() => {})
      clearSavedRefreshSession()
    }

    clearSession()
  }
  
  // 初始化時自動初始化WebSocket連接
  if (token.value) {
    // 如果已有token，自動初始化WebSocket
    initWebSocket()
  }
  
  return { 
    user, 
    isLoggedIn, 
    login, 
    logout, 
    validateToken,
    attemptTokenRefresh,
    hasSavedRefreshSession,
    initWebSocket,
    // 導出WebSocket服務實例供其他地方使用
    websocketService: adminWebSocketService
  }
}) 